import { useEffect, useState } from 'react';
import { useNavigate } from '@remix-run/react';

interface Notification {
  id: number;
  message: string;
  type: string;
  read: boolean;
  created_at: string;
  job_id?: number;
  job_title?: string;
  job_type?: string;
  data?: any;
}

interface NotificationListenerProps {
  userId: string;
  onNewNotification?: (notification: Notification) => void;
  onUnreadCountChange?: (count: number) => void;
}

/**
 * A component that listens for real-time notifications using SSE
 * This component doesn't render anything visible, it just sets up the SSE connection
 */
export default function NotificationListener({ 
  userId, 
  onNewNotification, 
  onUnreadCountChange 
}: NotificationListenerProps) {
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Don't set up SSE if we don't have a userId
    if (!userId) return;

    // Create a new EventSource connection to the SSE endpoint
    const sse = new EventSource(`/api/notifications/sse`);
    setEventSource(sse);

    // Handle the SSE connection opening
    sse.onopen = () => {
      console.log('SSE connection opened');
    };

    // Handle incoming messages
    sse.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle initial data
        if (data.type === 'initial') {
          setUnreadCount(data.unreadCount);
          if (onUnreadCountChange) {
            onUnreadCountChange(data.unreadCount);
          }
        }

        // Handle new notifications
        if (data.type === 'new_notification' && data.notification) {
          // Update unread count
          setUnreadCount(prev => prev + 1);
          if (onUnreadCountChange) {
            onUnreadCountChange(unreadCount + 1);
          }

          // Call the onNewNotification callback if provided
          if (onNewNotification) {
            onNewNotification(data.notification);
          }

          // Show a browser notification if supported and permission is granted
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('FerryPros', {
              body: data.notification.message,
              icon: '/favicon.ico'
            });
          }
        }
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    };

    // Handle errors
    sse.onerror = (error) => {
      console.error('SSE error:', error);

      // If we get a 401 Unauthorized error, redirect to login
      if ((error as any).status === 401) {
        navigate('/login');
      }

      // Close the connection on error
      sse.close();
      setEventSource(null);
    };

    // Clean up the SSE connection when the component unmounts
    return () => {
      sse.close();
      setEventSource(null);
    };
  }, [userId, navigate, onNewNotification, onUnreadCountChange, unreadCount]);

  // Request notification permission if supported
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // This component doesn't render anything visible
  return null;
}

/**
 * A component that displays a notification toast when a new notification is received
 */
export function NotificationToast({ 
  notification,
  onClose
}: { 
  notification: Notification | null,
  onClose: () => void
}) {
  useEffect(() => {
    // Auto-close the toast after 5 seconds
    if (notification) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notification, onClose]);

  if (!notification) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md w-full bg-white rounded-lg shadow-soft border border-gray-200 p-4 transform transition-transform duration-300 ease-in-out">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="font-medium text-gray-800">{notification.job_title || 'Notification'}</h3>
          <p className="text-sm text-bluegray-600 mt-1">{notification.message}</p>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(notification.created_at).toLocaleTimeString()}
          </p>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-600 hover:text-gray-800 transition-colors duration-200"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
